import React, { useContext, useState } from "react";
import styled from "styled-components";
import { showErrorNotification, showSuccessNotification } from "../utils/notification";
import { uploadImage } from "../axios/routes/image";
import { resolveDownloadSeatTokens } from "../utils/downloadSeatTokens";
import { getTemplateDesignName } from "../utils/ticketTemplate";
import { FileLoaderServiceInstance } from "../services/file-loader";
import { StorageServiceContext, StorageServiceInstance } from "../services/storage";
import { COLORS, FontFamilyTypeEnum } from "./styled";
import { TEMPLATE_TYPES } from "../shared/interfaces";
import { MomentServiceContext, MomentServiceInstance } from "../services/moment";
import { SaveOrShareModal } from "./SaveOrShareModal";
import { trackFanFileDownload, trackFanSocialShare } from "../utils/clientAnalytics";
import type { FanSocialShareChannel } from "../utils/clientAnalytics";

export type { FanSocialShareChannel };

// Styled Download Button (variant secondary = action row style: transparent bg, primary color border)
const ACTION_DARK = "#4A5568";
const DownloadButton = styled.button<{ backgroundColor?: string; $secondary?: boolean; $teamColor?: string }>`
  display: inline-flex;
  flex-direction: ${(p) => (p.$secondary ? "column" : "row")};
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$secondary ? "clamp(1px, 0.4vw, 2px)" : "8px")};
  width: ${(p) => (p.$secondary ? "100%" : "auto")};
  min-width: ${(p) => (p.$secondary ? "0" : "100px")};
  margin: 0;
  padding: ${(p) => (p.$secondary ? "clamp(4px, 1vw, 6px) clamp(6px, 1.5vw, 10px)" : "6px")};
  background-color: ${(p) => (p.$secondary ? "transparent" : (p.backgroundColor || "blue"))};
  color: ${(p) => (p.$secondary ? ACTION_DARK : "white")};
  font-size: ${(p) => (p.$secondary ? "clamp(9px, 2.2vw, 11px)" : "16px")};
  font-weight: 600;
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  border-radius: ${(p) => (p.$secondary ? "8px" : "10px")};
  cursor: pointer;
  border: ${(p) => (p.$secondary
    ? `1px solid ${(p.$teamColor || "#121B2F")}40`
    : "2px solid white")};
  box-shadow: none;
  text-transform: none;
  letter-spacing: ${(p) => (p.$secondary ? "0.02em" : "normal")};

  .download-icon {
    font-size: ${(p) => (p.$secondary ? "clamp(12px, 3vw, 15px)" : "18px")};
    line-height: 1;
  }

  .download-icon svg {
    width: ${(p) => (p.$secondary ? "clamp(14px, 3.5vw, 18px)" : "20px")};
    height: ${(p) => (p.$secondary ? "clamp(14px, 3.5vw, 18px)" : "20px")};
    display: block;
  }

  transition: border-color 0.2s ease, box-shadow 0.25s ease, transform 0.25s ease;

  &:disabled {
    background-color: gray;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    ${(p) => {
    const color = p.$teamColor || "#121B2F";
    return p.$secondary
      ? `border-color: ${color}; box-shadow: 0 0 12px ${color}40; transform: translateY(-2px);`
      : "box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12); transform: translateY(-3px);";
  }}
  }
  &:active:not(:disabled) {
    transform: translateY(0px);
    box-shadow: none;
  }
`;

export const isEmbeddedApp = () => {
  return (
    window !== window.parent // Detects if in iframe
    || window.navigator.userAgent.includes("wv") // Detects Android WebView
    || window.navigator.userAgent.includes("WebView") // General WebView detection
    || window.navigator.userAgent.includes("Yinzcam") // Explicit check for Yinzcam WebView
    || /(FBAN|FBAV|Instagram|Line|MicroMessenger|Twitter|Snapchat|Yinzcam)/i.test(window.navigator.userAgent) // Detects social media in-app browsers & Yinzcam
    || (/\(iP(hone|od|ad);/.test(window.navigator.userAgent) && !window.navigator.userAgent.includes("Safari")) // Detects iOS WebView (WKWebView/UIWebView)
  );
};

// FIXED: Android device detection
const isAndroidDevice = () => {
  return /Android/i.test(window.navigator.userAgent);
};

// FIXED: Proper base64ToBlobUrl function
const base64ToBlobUrl = (base64Data: string, mimeType = "image/png") => {
  const byteCharacters = atob(base64Data.split(",")[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};

// Function to detect MIME type from base64 data
const getMimeTypeFromBase64 = (base64Data: string): string => {
  const signature = base64Data.substring(0, 50);
  if (signature.includes("data:image/gif")) return "image/gif";
  if (signature.includes("data:image/png")) return "image/png";
  if (signature.includes("data:image/jpeg")) return "image/jpeg";
  if (signature.includes("data:image/jpg")) return "image/jpeg";

  // Fallback: check the actual data header
  const data = base64Data.split(",")[1];
  const binary = atob(data.substring(0, 20));

  // GIF signature: GIF87a or GIF89a
  if (binary.startsWith("GIF87a") || binary.startsWith("GIF89a")) return "image/gif";
  // PNG signature
  if (binary.charCodeAt(0) === 0x89 && binary.substring(1, 4) === "PNG") return "image/png";
  // JPEG signature
  if (binary.charCodeAt(0) === 0xFF && binary.charCodeAt(1) === 0xD8) return "image/jpeg";

  return "image/png"; // Default fallback
};

// Check if site is running on a Red Bulls subdomain
const isRedBullsSubdomain = () => {
  return window.location.hostname.startsWith("redbulls.");
};

// Check if site is running on a Ravens subdomain
const isRavensSubdomain = () => {
  return window.location.hostname.startsWith("ravens.");
};

// Check if site is running on a Bengals subdomain
const isBengalsSubdomain = () => {
  return window.location.hostname.startsWith("bengals.");
};

// Check if site is running on a Commanders subdomain
const isCommandersSubdomain = () => {
  return window.location.hostname.startsWith("commanders.");
};

// Check if site is running on a Falcons subdomain
const isFalconsSubdomain = () => {
  return window.location.hostname.startsWith("falcons.");
};

// Helper function to convert base64 to blob with proper MIME type
const base64ToBlob = (base64Data: string, mimeType: string) => {
  const byteCharacters = atob(base64Data.split(",")[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

export type PreparedMomentoResult = {
  finalImageUrl: string;
  actualMimeType: string;
  uniqueFilename: string;
  /** Used when uploading a copy to Videoboard S3. */
  uploadFileType?: "image" | "ticket" | "gif";
  ticketTemplateName?: string;
};

/** Upload prepared momento bytes to `preapproved_videoboard_Images/` (team must enable Send to Videoboard). */
export async function uploadPreparedMomentoToVideoboard(prepared: PreparedMomentoResult): Promise<void> {
  const {
    selectedGameData,
    selectedSection,
    selectedRow,
    selectedSeat,
    selectedCustomerId,
    selectedEmail,
    selectedSpecialSection,
    selectedSectionIsSpecial,
    selectedPlaceX,
    selectedPlaceY,
  } = StorageServiceInstance;
  const stadium = selectedGameData?.stadium ?? "Unknown_Stadium";
  const team1Name = selectedGameData?.team1?.name || "Team1";
  const team2Name = selectedGameData?.team2?.name || "Team2";
  const { section, row, seat } = resolveDownloadSeatTokens({
    selectedSection,
    selectedRow,
    selectedSeat,
    selectedSpecialSection,
    selectedSectionIsSpecial,
    selectedPlaceX,
    selectedPlaceY,
    selectedCustomerId,
    selectedEmail,
    isGa: StorageServiceInstance.isGaMomentContext(),
  });
  const fileType = prepared.uploadFileType
    ?? (prepared.actualMimeType.includes("gif") ? "gif" : "image");
  await uploadImage({
    imageUrl: prepared.finalImageUrl,
    action: "videoboard",
    stadium,
    team1Name,
    team2Name,
    gameStartDateMs: selectedGameData?.startDate ?? null,
    gameId: selectedGameData?._id,
    customerId: selectedCustomerId,
    email: selectedEmail,
    section,
    row,
    seat,
    fileType,
    ticketTemplateName: prepared.ticketTemplateName,
  });
}

export type MomentoDownloadTicketViewMode =
  | "Image"
  | "ImageScoreboard"
  | "Ticket1"
  | "Ticket2"
  | "Ticket3"
  | "GIF";

/** Prepares image for save/share: uploads to S3, returns image data. Used before showing SaveOrShare modal. */
export const prepareMomentoForSaveOrShare = async (params: {
  momentId: string;
  momentoIndex: number;
  ticketViewMode: MomentoDownloadTicketViewMode;
  gifImage?: string;
  showWatermark?: boolean;
  watermarkLogoOpacity?: number;
}): Promise<PreparedMomentoResult> => {
  const {
    momentId, momentoIndex, ticketViewMode, gifImage, showWatermark = false, watermarkLogoOpacity = 0.4,
  } = params;
  const {
    selectedEmail,
    selectedCustomerId,
    selectedGameData,
    selectedSection,
    selectedRow,
    selectedSeat,
    selectedSpecialSection,
    selectedSectionIsSpecial,
    selectedPlaceX,
    selectedPlaceY,
  } = StorageServiceInstance;
  const { section, row, seat } = resolveDownloadSeatTokens({
    selectedSection,
    selectedRow,
    selectedSeat,
    selectedSpecialSection,
    selectedSectionIsSpecial,
    selectedPlaceX,
    selectedPlaceY,
    selectedCustomerId,
    selectedEmail,
    isGa: StorageServiceInstance.isGaMomentContext(),
  });
  const stadium = selectedGameData?.stadium ?? "Unknown_Stadium";
  const team1Name = selectedGameData?.team1?.name || "Team1";
  const team2Name = selectedGameData?.team2?.name || "Team2";

  let finalImageUrl = "";
  let actualMimeType = "image/png";
  let actualExtension = "png";
  let uploadTicketTemplateName: string | undefined;

  StorageServiceInstance.saveMoment(momentId);
  MomentServiceInstance.setSelectedMomentByMomentId(momentId);
  if (ticketViewMode === "Image" || ticketViewMode === "ImageScoreboard") {
    MomentServiceInstance.selectMomentoSlide(momentoIndex);
    const showHeader = ticketViewMode === "ImageScoreboard"
      || StorageServiceInstance.selectedShowMomentCaption;
    finalImageUrl = await FileLoaderServiceInstance.prepareSingleMomentoImage(showHeader);
    actualMimeType = getMimeTypeFromBase64(finalImageUrl);
    actualExtension = actualMimeType.split("/")[1];
  } else if (ticketViewMode === "GIF" && gifImage) {
    finalImageUrl = gifImage;
    actualMimeType = getMimeTypeFromBase64(finalImageUrl);
    actualExtension = "gif";
  } else {
    let templateType = TEMPLATE_TYPES.MAIN;
    if (ticketViewMode === "Ticket2") templateType = TEMPLATE_TYPES.FAN1st;
    if (ticketViewMode === "Ticket3") templateType = TEMPLATE_TYPES.SPECIAL_SECTION;
    uploadTicketTemplateName = getTemplateDesignName(templateType, selectedGameData || undefined);
    const imageLinkInfo = await FileLoaderServiceInstance.saveTicketImage(
      momentoIndex,
      templateType,
      true,
      undefined,
      undefined,
      showWatermark,
      watermarkLogoOpacity,
    );
    finalImageUrl = imageLinkInfo?.dataUrl;
    actualMimeType = getMimeTypeFromBase64(finalImageUrl);
    actualExtension = actualMimeType.split("/")[1];
  }

  const timestamp = Date.now();
  const uniqueFilename = `${section}_${row}_${seat}_${timestamp}.${actualExtension}`;

  let uploadFileType: "image" | "ticket" | "gif" = "image";
  if (ticketViewMode === "GIF") uploadFileType = "gif";
  if (
    ticketViewMode !== "Image"
    && ticketViewMode !== "ImageScoreboard"
    && ticketViewMode !== "GIF"
  ) {
    uploadFileType = "ticket";
  }

  // Do not block save/share UX on telemetry upload; fire-and-forget.
  // This call can be slow on mobile networks and was delaying every download flow,
  // especially bulk/select-multiple where it runs many times.
  uploadImage({
    imageUrl: finalImageUrl,
    action: "download",
    stadium,
    team1Name,
    team2Name,
    gameStartDateMs: selectedGameData?.startDate ?? null,
    gameId: selectedGameData?._id,
    customerId: selectedCustomerId,
    email: selectedEmail,
    section,
    row,
    seat,
    fileType: uploadFileType,
    ticketTemplateName: uploadTicketTemplateName,
  }).catch((err) => {
    console.warn("Download telemetry upload failed:", err);
  });

  return {
    finalImageUrl,
    actualMimeType,
    uniqueFilename,
    uploadFileType,
    ticketTemplateName: uploadTicketTemplateName,
  };
};

/** Converts a signed image URL (e.g. from profile downloads) to PreparedMomentoResult for SaveOrShareModal. */
export const prepareSignedUrlForSaveOrShare = async (
  signedUrl: string,
  s3Key?: string,
): Promise<PreparedMomentoResult> => {
  const response = await fetch(signedUrl);
  const blob = await response.blob();
  const actualMimeType = blob.type || "image/jpeg";
  const ext = actualMimeType.split("/")[1] || "jpg";
  const uniqueFilename = (s3Key || "moment").split("/").pop() || `moment.${ext}`;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        finalImageUrl: reader.result as string,
        actualMimeType,
        uniqueFilename,
        uploadFileType: "image",
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const DEFAULT_SHARE_URL = "https://app.mymomento.com";

/** Build share URL from team hostname (e.g. seahawks -> https://seahawks.mymomento.com) */
export const getShareUrl = (hostname?: string): string => {
  if (hostname?.trim()) {
    return `https://${hostname.trim().toLowerCase()}.mymomento.com`;
  }
  return DEFAULT_SHARE_URL;
};

/** Display host without protocol (e.g. cubs.mymomento.com). */
export const getShareDisplayHost = (hostname?: string): string => {
  if (hostname?.trim()) {
    return `${hostname.trim().toLowerCase()}.mymomento.com`;
  }
  return "app.mymomento.com";
};

/** Share-sheet message for the Fan Sharing Prompt. */
export const getFanSharingPromptText = (hostname?: string): string => (
  `Check out my photo from the game! If you were there too, find your Momento at ${getShareDisplayHost(hostname)}`
);

/** Build share text: caption + hashtag (no URL – URL is passed separately where needed) */
export const getShareText = (
  teamName?: string,
  shareHashtag?: string,
  hostname?: string,
): string => {
  const hashtag = (shareHashtag?.replace(/^#/, "").trim() || "MyMomento").toLowerCase();
  const teamLabel = teamName ? `${teamName} game` : "the game";
  return `Found myself in the crowd at the ${teamLabel} 👀\n\n#${hashtag}`;
};

/** Drop a URL already stored in the caption so the share sheet does not repeat it. */
export const omitShareUrlFromText = (text: string, url?: string): string => {
  const link = (url || "").trim();
  if (!link) return text.trim();
  return text
    .split(link)
    .join("")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/** Build full share text with URL appended (for native share sheets that don't have a separate URL field) */
export const getShareTextWithUrl = (
  teamName?: string,
  shareHashtag?: string,
  hostname?: string,
): string => {
  const text = getShareText(teamName, shareHashtag, hostname);
  const url = getShareUrl(hostname);
  return `${text}\n${url}`;
};

/** Opens native share sheet (Instagram, Snapchat, Facebook, Twitter, etc.) - user picks app to share with image + text. */
export const executeNativeShare = (
  prepared: PreparedMomentoResult,
  shareText?: string,
  channel: FanSocialShareChannel = "native",
  shareUrl?: string,
): void => {
  trackFanSocialShare(channel, { share_method: "navigator_share" });
  const { finalImageUrl, actualMimeType, uniqueFilename } = prepared;
  const ext = actualMimeType.split("/")[1] || "png";
  const filename = uniqueFilename || `momento.${ext}`;
  FileLoaderServiceInstance.shareTicketDirectToNative(
    finalImageUrl,
    filename,
    actualMimeType,
    shareText,
    shareUrl,
  ).catch(() => {
    // User cancelled or share failed – ignore
  });
};

/** Opens native share with image (Facebook, Twitter, etc. from share sheet). When prepared exists, uses navigator.share so user can pick app and post with image. */
const executeNativeShareWithImage = (
  prepared: PreparedMomentoResult,
  shareTextWithUrl: string,
  channel: FanSocialShareChannel,
): void => {
  executeNativeShare(prepared, shareTextWithUrl, channel);
};

/** Opens Facebook share. When prepared exists, uses native share (image + text) so user can post with photo. Otherwise URL-only. */
export const executeFacebookShare = (
  shareUrl?: string,
  shareTextWithUrl?: string,
  prepared?: PreparedMomentoResult | null,
): void => {
  if (prepared && navigator.share) {
    executeNativeShareWithImage(prepared, shareTextWithUrl || getShareTextWithUrl(), "facebook");
    return;
  }
  trackFanSocialShare("facebook", { share_method: "facebook_web_sharer" });
  const url = encodeURIComponent(shareUrl || DEFAULT_SHARE_URL);
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank", "width=600,height=400");
};

/** Opens Twitter/X share. When prepared exists, uses native share (image + text) so user can post with photo. Otherwise web intent (text only). */
export const executeTwitterShare = async (
  shareUrl?: string,
  shareText?: string,
  prepared?: PreparedMomentoResult | null,
): Promise<void> => {
  if (prepared && navigator.share) {
    const fullText = shareText
      ? `${shareText}\n${shareUrl || DEFAULT_SHARE_URL}`
      : `Found myself in the crowd 👀\n\n#mymomento\n${shareUrl || DEFAULT_SHARE_URL}`;
    executeNativeShareWithImage(prepared, fullText, "twitter");
    return;
  }
  trackFanSocialShare("twitter", { share_method: "twitter_web_intent" });
  const fullText = shareText
    ? `${shareText}\n${shareUrl || DEFAULT_SHARE_URL}`
    : `Found myself in the crowd 👀\n\n#mymomento\n${shareUrl || DEFAULT_SHARE_URL}`;
  const textParam = encodeURIComponent(fullText);
  window.open(`https://twitter.com/intent/tweet?text=${textParam}`, "_blank", "width=600,height=400");
};

const isShareAbortError = (err: unknown): boolean => (
  !!err && typeof err === "object" && (err as { name?: string }).name === "AbortError"
);

/** Executes share or save based on user choice. Call after SaveOrShare modal. shareText (caption + hashtag + URL) is passed when saving so it appears in the share sheet. */
export const executeSaveOrShare = async (
  prepared: PreparedMomentoResult,
  action: "share" | "save",
  shareText?: string,
  popupOptions?: {
    suppressConfiguredPopups?: boolean;
    shareUrl?: string;
    onDeviceSave?: (saved?: PreparedMomentoResult) => void;
  },
): Promise<void> => {
  const { finalImageUrl, actualMimeType, uniqueFilename } = prepared;

  if (action === "share") {
    executeNativeShare(prepared, shareText, "native");
    return;
  }

  const saveExt = (uniqueFilename.split(".").pop() || "png").toLowerCase();
  const saveAssetType = saveExt === "gif" ? "gif" : saveExt === "mp4" ? "video" : "image";
  trackFanFileDownload({
    file_name: uniqueFilename,
    asset_type: saveAssetType,
    ui_surface: "save_or_share_modal",
  });

  const completeDeviceSave = () => {
    const suppressTeamPopup = !!popupOptions?.suppressConfiguredPopups
      || StorageServiceInstance.willShowFanSharingPrompt(prepared.uploadFileType)
      || StorageServiceInstance.willShowGamedayStoryPrompt(prepared.uploadFileType);
    if (!suppressTeamPopup) {
      if (prepared.uploadFileType === "ticket") {
        StorageServiceInstance.requestTeamPopupForOccurrence("momento_ticket_download");
      } else {
        StorageServiceInstance.requestTeamPopupForOccurrence("momento_image_download");
      }
    }
    popupOptions?.onDeviceSave?.(prepared);
    StorageServiceInstance.requestFanSharingPromptAfterSave(prepared);
    StorageServiceInstance.requestGamedayStoryPromptAfterSave(prepared);
  };

  // Save to device
  const isMobile = "ontouchstart" in window || window.navigator.maxTouchPoints > 0;
  if (!isMobile) {
    const blob = base64ToBlob(finalImageUrl, actualMimeType);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = uniqueFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
    completeDeviceSave();
    return;
  }

  const shareUrl = popupOptions?.shareUrl?.trim();
  if (navigator.share) {
    const blob = base64ToBlob(finalImageUrl, actualMimeType);
    const file = new File([blob], uniqueFilename, { type: actualMimeType });
    const caption = shareText || getShareTextWithUrl();
    const text = shareUrl ? omitShareUrlFromText(caption, shareUrl) : caption;
    const sharePayload: ShareData = { files: [file], text, title: "Momento" };
    if (shareUrl) {
      sharePayload.url = shareUrl;
    }
    try {
      await navigator.share(sharePayload);
      completeDeviceSave();
    } catch (err) {
      if (isShareAbortError(err)) {
        return;
      }
      executeNativeShare(prepared, shareText, "native", shareUrl);
      completeDeviceSave();
    }
    return;
  }

  executeNativeShare(prepared, shareText, "native", shareUrl);
  completeDeviceSave();
};

/** Webview: standard sharing (Yinzcam deep link or shareTicket). Non-webview: returns prepared data for modal. */
export const performMomentoDownload = async (params: {
  momentId: string;
  momentoIndex: number;
  ticketViewMode: MomentoDownloadTicketViewMode;
  gifImage?: string;
  showWatermark?: boolean;
  watermarkLogoOpacity?: number;
}, options?: {
  action?: "share" | "save";
  suppressConfiguredPopups?: boolean;
  onDeviceSave?: (saved?: PreparedMomentoResult) => void;
}): Promise<PreparedMomentoResult | void> => {
  const prepared = await prepareMomentoForSaveOrShare(params);
  const { finalImageUrl } = prepared;

  const inWebView = isEmbeddedApp();
  const isYinzcam = (isRedBullsSubdomain() || isRavensSubdomain() || isBengalsSubdomain() || isFalconsSubdomain() || isCommandersSubdomain()) && inWebView;

  if (isYinzcam) {
    let yinzcamDownloadUrl = "";
    if (isRedBullsSubdomain()) yinzcamDownloadUrl = `yc-mls-rbny://action/DOWNLOAD_FILE?url=${encodeURIComponent(finalImageUrl)}`;
    else if (isRavensSubdomain()) yinzcamDownloadUrl = `yc-nfl-bal://action/DOWNLOAD_FILE?url=${encodeURIComponent(finalImageUrl)}`;
    else if (isBengalsSubdomain()) yinzcamDownloadUrl = `yc-nfl-cin://action/DOWNLOAD_FILE?url=${encodeURIComponent(finalImageUrl)}`;
    else if (isFalconsSubdomain()) yinzcamDownloadUrl = `yc-nfl-atl://action/DOWNLOAD_FILE?url=${encodeURIComponent(finalImageUrl)}`;
    else if (isCommandersSubdomain()) yinzcamDownloadUrl = `yc-nfl-was://action/DOWNLOAD_FILE?url=${encodeURIComponent(finalImageUrl)}`;

    trackFanFileDownload({ ui_surface: "embedded_yinzcam", asset_type: "image" });
    if (isAndroidDevice()) {
      FileLoaderServiceInstance.shareTicket(finalImageUrl);
    } else {
      window.location.href = yinzcamDownloadUrl;
    }
    options?.onDeviceSave?.(prepared);
    StorageServiceInstance.requestFanSharingPromptAfterSave(prepared);
    StorageServiceInstance.requestGamedayStoryPromptAfterSave(prepared);
    return undefined;
  }

  if (inWebView || options?.action) {
    const { selectedGameData } = StorageServiceInstance;
    const shareText = getShareTextWithUrl(
      selectedGameData?.team1?.name,
      selectedGameData?.team1?.shareHashtag,
      selectedGameData?.team1?.hostname,
    );
    executeSaveOrShare(prepared, options?.action ?? "share", shareText, {
      suppressConfiguredPopups: options?.action === "save" && options?.suppressConfiguredPopups,
      shareUrl: getShareUrl(selectedGameData?.team1?.hostname),
      onDeviceSave: options?.action === "save" ? options?.onDeviceSave : undefined,
    });
    return undefined;
  }

  return prepared;
};

const MomentoDownloadButton: React.FC<{
  momentId: string;
  momentoIndex: number;
  backgroundColor?: string;
  ticketViewMode: MomentoDownloadTicketViewMode;
  gifImage?: string;
  onShowOptInModal?: () => void;
  showWatermark?: boolean;
  watermarkLogoOpacity?: number;
  variant?: "primary" | "secondary";
  teamColor?: string;
}> = ({
  momentId,
  momentoIndex,
  backgroundColor = "blue",
  ticketViewMode,
  gifImage,
  onShowOptInModal,
  showWatermark,
  watermarkLogoOpacity = 0.4,
  variant = "primary",
  teamColor,
}) => {
  const {
    selectedEmail, selectedGameData, selectedRow, selectedSeat, selectedSection,
    enablePaidDownload: enablePaidDownloadContext,
    team1Color,
  } = useContext(StorageServiceContext);
  const shareHashtag = selectedGameData?.team1?.shareHashtag;
  const shareUrl = getShareUrl(selectedGameData?.team1?.hostname);
  const nativeShareText = getShareTextWithUrl(
    selectedGameData?.team1?.name,
    shareHashtag,
    selectedGameData?.team1?.hostname,
  );
  const { isDownloadPaid } = useContext(MomentServiceContext);

  const [loading, setLoading] = useState(false);
  const [saveOrShareModal, setSaveOrShareModal] = useState<{ visible: boolean; prepared: PreparedMomentoResult | null }>({
    visible: false,
    prepared: null,
  });
  const [videoboardSending, setVideoboardSending] = useState(false);

  const enablePaidDownload = enablePaidDownloadContext;

  const notifyKeepsakeDownloadPopup = () => {
    StorageServiceInstance.requestKeepsakeDownloadPopupAfterSave(ticketViewMode);
  };

  const handleShareOrDownload = async () => {
    setLoading(true);

    try {
      const userId = selectedEmail || "Unknown_User";
      const { section, row, seat } = resolveDownloadSeatTokens({
        selectedSection,
        selectedRow,
        selectedSeat,
        selectedSpecialSection: StorageServiceInstance.selectedSpecialSection,
        selectedSectionIsSpecial: StorageServiceInstance.selectedSectionIsSpecial,
        selectedPlaceX: StorageServiceInstance.selectedPlaceX,
        selectedPlaceY: StorageServiceInstance.selectedPlaceY,
        selectedCustomerId: StorageServiceInstance.selectedCustomerId,
        selectedEmail,
        isGa: StorageServiceInstance.isGaMomentContext(),
      });
      const gameId = selectedGameData?._id;

      if (showWatermark && onShowOptInModal) {
        setLoading(false);
        onShowOptInModal();
        return;
      }

      if (enablePaidDownload && !isDownloadPaid) {
        const isDownloadPaidStatus = await MomentServiceInstance.getDownloadPaidStatus(userId, gameId, section, row, seat);
        if (!isDownloadPaidStatus) {
          setLoading(false);
          window.location.href = `https://shop.mymomento.com/cart/45501647913253:1?attributes[email]=${selectedEmail}&attributes[game_id]=${gameId}&attributes[section]=${section}&attributes[row]=${row}&attributes[seat]=${seat}`;
          return;
        }
      }

      const suppressConfiguredPopups = StorageServiceInstance.willShowKeepsakeDownloadPopup(
        ticketViewMode,
      );
      const result = await performMomentoDownload({
        momentId,
        momentoIndex,
        ticketViewMode,
        gifImage,
        showWatermark: !!showWatermark,
        watermarkLogoOpacity,
      }, {
        suppressConfiguredPopups,
        onDeviceSave: notifyKeepsakeDownloadPopup,
      });

      if (result) {
        setSaveOrShareModal({ visible: true, prepared: result });
      }
    } catch (error) {
      console.error("Error during download:", error);
      showErrorNotification("An error occurred while processing the momento.");
    }

    setLoading(false);
  };

  const handleSaveToDevice = () => {
    if (saveOrShareModal.prepared) {
      const suppressConfiguredPopups = StorageServiceInstance.willShowKeepsakeDownloadPopup(
        ticketViewMode,
      );
      executeSaveOrShare(saveOrShareModal.prepared, "save", nativeShareText, {
        shareUrl,
        suppressConfiguredPopups,
        onDeviceSave: notifyKeepsakeDownloadPopup,
      });
    }
    setSaveOrShareModal({ visible: false, prepared: null });
  };

  const handleSendToVideoboard = async () => {
    if (!saveOrShareModal.prepared) return;
    setVideoboardSending(true);
    try {
      await uploadPreparedMomentoToVideoboard(saveOrShareModal.prepared);
      showSuccessNotification(
        "Sent to Videoboard",
        "Your image has been sent to the Videoboard. Stay tuned to see your Momento on screen!",
        6,
      );
      setSaveOrShareModal({ visible: false, prepared: null });
    } catch (err) {
      console.error(err);
      showErrorNotification("Could not send to Videoboard. Please try again.");
    } finally {
      setVideoboardSending(false);
    }
  };

  // Determine button text based on subdomain
  const getButtonText = () => {
    if (loading) return "Processing...";
    if (isRavensSubdomain() || isBengalsSubdomain() || isCommandersSubdomain()) return "Free Download";
    return "Download";
  };

  return (
    <>
      <DownloadButton
        onClick={handleShareOrDownload}
        disabled={loading}
        backgroundColor={variant === "secondary" ? undefined : backgroundColor}
        $secondary={variant === "secondary"}
        $teamColor={variant === "secondary" ? teamColor : undefined}
      >
        {variant === "secondary" ? (
          <>
            <span className="download-icon" aria-hidden>
              <svg viewBox="0 0 43 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M30.8776 12.3582L23.0375 19.9811V0H19.0678L19.0678 19.9811L11.2276 12.3582L8.42106 15.087L21.0526 27.3684L33.6842 15.087L30.8776 12.3582ZM37.8947 35.7895H4.21053V25.2632H0V37.8947C0 38.4531 0.221804 38.9886 0.616617 39.3834C1.01143 39.7782 1.54691 40 2.10526 40H40C40.5584 40 41.0938 39.7782 41.4886 39.3834C41.8835 38.9886 42.1053 38.4531 42.1053 37.8947V25.2632H37.8947V35.7895Z" fill="currentColor" />
              </svg>
            </span>
            {loading ? "Processing..." : "Download/Share"}
          </>
        ) : (
          getButtonText()
        )}
      </DownloadButton>
      <SaveOrShareModal
        visible={saveOrShareModal.visible}
        previewImageUrl={saveOrShareModal.prepared?.finalImageUrl}
        shareHashtag={shareHashtag}
        onSaveToDevice={handleSaveToDevice}
        showSendToVideoboard={!!selectedGameData?.team1?.sendToVideoboard}
        onSendToVideoboard={handleSendToVideoboard}
        sendToVideoboardLoading={videoboardSending}
        onCancel={() => setSaveOrShareModal({ visible: false, prepared: null })}
        buttonColor={team1Color || COLORS.BLUE_MAIN}
      />
    </>
  );
};

export default MomentoDownloadButton;
